/**
 * Platforms a video can be imported from by pasting a link.
 *
 * An import is an upload that the server performs instead of the browser: the
 * worker fetches the video, stores it exactly where an upload would land, and
 * hands it to the same transcribe and transcode jobs. Nothing downstream —
 * player, transcript, translations, chapters, Studio, exports, retention —
 * knows or cares that the file came from a link.
 *
 * This registry is the one place a platform is defined. The web app reads it
 * to recognise a link as it is typed, the API reads it to refuse anything
 * else, and the worker reads it to decide which downloader extractors may run.
 * Adding a platform is an entry here plus its id in `platformSchema`.
 *
 * Links are rebuilt from the parts we recognise rather than passed through.
 * That strips playlist and tracking parameters (a `list=` would otherwise turn
 * one video into a channel's worth), and it means the downloader is only ever
 * handed a URL of a shape we chose — never an arbitrary address a user typed,
 * which on a server is how a "fetch this link" feature becomes a way to make
 * the server request internal hosts.
 */

export type ImportSource = {
  /** Stored as `meetings.platform`. Must also appear in `platformSchema`. */
  id: string;
  /** For people: badges, the library, error sentences. */
  label: string;
  /**
   * yt-dlp extractors allowed to handle this source's links.
   *
   * Passed as `--use-extractors`, so a link can never fall through to the
   * generic extractor, which downloads from whatever page it is pointed at.
   */
  extractors: readonly string[];
  /** Shown as the input's example. */
  example: string;
  /**
   * Hosts of the platform's own link shortener (`lnkd.in`). A link on one of
   * these is accepted on its shape alone and resolved by the worker, which
   * follows its redirect — staying on this platform's hosts — to the real
   * post before anything is downloaded. The API never fetches it.
   */
  shortLinkHosts?: readonly string[];
  /** The canonical link for a recognised URL, or null if it is not one of ours. */
  canonicalize: (url: URL) => string | null;
};

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

/** Every way YouTube writes a link to a single video. */
function youtubeVideoId(url: URL): string | null {
  const host = url.hostname.toLowerCase().replace(/^(www|m)\./, "");
  const segments = url.pathname.split("/").filter(Boolean);

  if (host === "youtu.be") {
    return segments[0] && YOUTUBE_ID.test(segments[0]) ? segments[0] : null;
  }

  if (host !== "youtube.com" && host !== "youtube-nocookie.com") return null;

  if (segments[0] === "watch") {
    const id = url.searchParams.get("v");
    return id && YOUTUBE_ID.test(id) ? id : null;
  }

  // /shorts/ID, /live/ID (a finished stream is an ordinary video), /embed/ID
  if (["shorts", "live", "embed", "v"].includes(segments[0] ?? "")) {
    const id = segments[1];
    return id && YOUTUBE_ID.test(id) ? id : null;
  }

  return null;
}

/**
 * LinkedIn posts, in the two shapes the downloader supports.
 *
 * `/posts/<slug>-<id>-<4 chars>` is kept whole rather than rebuilt from the id:
 * that id is sometimes a `ugcPost` rather than an `activity`, and turning it
 * into a feed link would point at a post that does not exist. Mirrors yt-dlp's
 * own patterns, so a link we accept is a link it can fetch.
 */
function linkedinPostUrl(url: URL): string | null {
  const host = url.hostname.toLowerCase();

  // `lnkd.in/p/AbC123` and `lnkd.in/AbC123`: LinkedIn's share links. Kept as
  // a short link; the worker resolves it (see `shortLinkHosts`).
  if (host === "lnkd.in") {
    const path = url.pathname.replace(/\/+$/, "");
    return /^\/(?:p\/)?[A-Za-z0-9_-]{4,64}$/.test(path) ? `https://lnkd.in${path}` : null;
  }

  if (host !== "linkedin.com" && !host.endsWith(".linkedin.com")) return null;

  const path = url.pathname.replace(/\/+$/, "");

  const post = /^\/posts\/([^/]+-\d+-\w{4})$/.exec(path);
  if (post) return `https://www.linkedin.com/posts/${post[1]}`;

  const activity = /^\/feed\/update\/urn:li:activity:(\d+)$/.exec(path);
  if (activity) {
    return `https://www.linkedin.com/feed/update/urn:li:activity:${activity[1]}/`;
  }

  return null;
}

export const IMPORT_SOURCES = [
  {
    id: "youtube",
    label: "YouTube",
    extractors: ["youtube"],
    example: "https://www.youtube.com/watch?v=…",
    canonicalize: (url) => {
      const id = youtubeVideoId(url);
      return id ? `https://www.youtube.com/watch?v=${id}` : null;
    },
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    extractors: ["linkedin"],
    example: "https://www.linkedin.com/posts/…",
    shortLinkHosts: ["lnkd.in"],
    canonicalize: linkedinPostUrl,
  },
] as const satisfies readonly ImportSource[];

export type ImportSourceId = (typeof IMPORT_SOURCES)[number]["id"];

/** Longer than any real video link, short enough not to store an essay. */
export const IMPORT_URL_MAX_LENGTH = 2048;

export type RecognisedImport = {
  source: ImportSource & { id: ImportSourceId };
  /** The canonical link — what gets stored and what the downloader receives. */
  url: string;
};

/**
 * Which platform a pasted link belongs to, and its canonical form.
 *
 * Null for anything not recognised. Tolerates a missing scheme, because
 * people paste `youtu.be/…` from share sheets, and surrounding whitespace.
 * Only http(s) is ever accepted.
 */
export function recogniseImportUrl(input: string): RecognisedImport | null {
  const trimmed = input.trim();
  if (!trimmed || trimmed.length > IMPORT_URL_MAX_LENGTH) return null;

  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }

  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  // Credentials or a port in a video link are never legitimate.
  if (url.username || url.password || url.port) return null;

  for (const source of IMPORT_SOURCES) {
    const canonical = source.canonicalize(url);
    if (canonical) return { source, url: canonical };
  }

  return null;
}

/** True when `url` is a platform short link that must be resolved before fetching. */
export function isShortImportLink(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return IMPORT_SOURCES.some((source) =>
      ((source as ImportSource).shortLinkHosts ?? []).includes(host),
    );
  } catch {
    return false;
  }
}

/** The import source a stored `platform` refers to, if it is one. */
export function importSourceFor(
  platform: string,
): (ImportSource & { id: ImportSourceId }) | null {
  return IMPORT_SOURCES.find((source) => source.id === platform) ?? null;
}

/** "YouTube or LinkedIn" — for sentences that list what is supported. */
export function supportedImportLabels(): string {
  const labels = IMPORT_SOURCES.map((source) => source.label);
  return labels.length <= 1
    ? (labels[0] ?? "")
    : `${labels.slice(0, -1).join(", ")} or ${labels.at(-1)}`;
}

export const IMPORT_STAGES = ["checking", "downloading", "storing"] as const;
export type ImportStage = (typeof IMPORT_STAGES)[number];

/**
 * What an import is doing, as a badge and a sentence.
 *
 * The one place this wording lives, like `meaningOf` for bot statuses: the
 * API derives `statusLabel` and `statusMessage` from it, so the library, the
 * recording page and the import dialog all say the same thing.
 */
export function describeImportStage(
  stage: string | null,
  sourceLabel: string,
): { label: string; message: string } | null {
  switch (stage) {
    case "checking":
      return {
        label: "Checking link",
        message: `Looking for the video on ${sourceLabel}.`,
      };
    case "downloading":
      return {
        label: "Downloading",
        message: `Downloading the video from ${sourceLabel}. This page updates when it is done.`,
      };
    case "storing":
      return {
        label: "Saving",
        message: "Saving the video to your library.",
      };
    default:
      return null;
  }
}
